/*
 * Purpose: AI connector factory — build new AutoBlocks-owned connectors when gaps exist
 * Last modified: 2026-06-25 | Agent
 * Completeness: 85
 */
import { z } from 'zod';
import { resolveSecret } from '@/lib/resolveSecret';
import {
  findConnectorsForOutcome,
  listConnectorCatalogForPrompt,
  useConnectorLibraryStore,
} from './store';
import { matchOutcomeSeed } from '@/seeds/averageOutcomes';
import { aiConnectorSchema, connectorFactoryResponseSchema } from './connectorSchema';
import type { AutoBlocksConnector } from './types';
import { readEnvOr } from '@/lib/runtimeEnv';

const MODEL = readEnvOr('VITE_OPENAI_MODEL', 'gpt-4o-mini');

function toAutoBlocksConnector(payload: z.infer<typeof aiConnectorSchema>): AutoBlocksConnector {
  return {
    ...payload,
    vendor: 'ai_generated',
    version: 1,
    createdAt: new Date().toISOString(),
  };
}

export interface ConnectorFactoryResult {
  connectorIds: string[];
  builtConnectors: AutoBlocksConnector[];
  rationale?: string;
}

function ruleBasedFactory(outcome: string): ConnectorFactoryResult {
  const seed = matchOutcomeSeed(outcome);
  if (seed) {
    return { connectorIds: [...seed.expectedConnectorIds], builtConnectors: [] };
  }
  const matched = findConnectorsForOutcome(outcome);
  const ids = matched.length > 0 ? matched.map((c) => c.id) : ['ab.logic', 'ab.http', 'ab.notify'];
  return { connectorIds: [...new Set(ids)], builtConnectors: [] };
}

export async function buildConnectorsForOutcome(
  outcome: string,
  interpretedGoal: string
): Promise<ConnectorFactoryResult> {
  const rule = ruleBasedFactory(outcome);
  const apiKey = resolveSecret('OPENAI_API_KEY');
  if (!apiKey) return rule;

  const catalog = listConnectorCatalogForPrompt();

  try {
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: MODEL,
        temperature: 0.25,
        response_format: { type: 'json_object' },
        messages: [
          {
            role: 'system',
            content: `You are the AutoBlocks Connector Factory. You OWN integration — never reference Make, Zapier, or n8n.

Existing AutoBlocks connector library:
${catalog}

When an outcome needs capability not covered:
1. Prefer existing connector IDs in connectorIds
2. If gap remains, define newConnectors as AutoBlocks-native connectors (id prefix ab.custom.*)
3. Each new connector must map capabilities to blockType/blockSubtype (trigger|tool|action + subtype)
4. setupSteps are AutoBlocks-only (Connect X in AutoBlocks, add secret Y, configure node Z)
5. patternRef may note internal pattern e.g. "inspired-by: webhook-ingest" — never a product name

Return JSON: { connectorIds: string[], newConnectors: [...], rationale: string }`,
          },
          {
            role: 'user',
            content: `OUTCOME: ${outcome}\nGOAL: ${interpretedGoal}`,
          },
        ],
      }),
    });

    if (!response.ok) return rule;

    const data = (await response.json()) as {
      choices?: Array<{ message?: { content?: string } }>;
    };
    const content = data.choices?.[0]?.message?.content;
    if (!content) return rule;

    const parsed = connectorFactoryResponseSchema.parse(JSON.parse(content));
    const builtConnectors: AutoBlocksConnector[] = [];

    for (const raw of parsed.newConnectors) {
      const valid = aiConnectorSchema.parse(raw);
      const connector = toAutoBlocksConnector(valid);
      useConnectorLibraryStore.getState().register(connector);
      builtConnectors.push(connector);
    }

    const connectorIds = [...new Set([...parsed.connectorIds, ...builtConnectors.map((c) => c.id)])];

    return {
      connectorIds: connectorIds.length ? connectorIds : rule.connectorIds,
      builtConnectors,
      rationale: parsed.rationale,
    };
  } catch {
    return rule;
  }
}
